// Theme system extracted from Themes/theme1/project/index.html (Rheora Global Design System).
// Five preset themes, twelve gradient backgrounds, eight SVG patterns, six typefaces.
// All values feed into CSS custom properties on :root via useTweaks().
//
// 2026-10-01: ERCOT Grid diverges from the prototype on purpose. It dropped the
// engineering-grid pattern, lost the mint radial from its `arctic-grid` ground,
// and moved to the `console` look (opaque paper cards, a rail painted in the
// brand colour, tighter radii) — see `ThemeLook` below and the
// `body[data-look='console']` block in styles/crm.css. The prototype files are
// untouched; this is the operator's call, not a transcription error.

/**
 * How a preset's surfaces are built.
 *
 * `glass` is the prototype's look: translucent cards blurred over a gradient,
 * a frosted light rail, 24px radii. `console` is the operator-tool look ERCOT
 * Grid moved to: opaque cards with a real edge and shadow, a rail painted in
 * the brand colour, 16px radii. useTweaks() writes it as `body[data-look]` and
 * as the `--r-*` radius tokens; styles/crm.css styles the rest under that
 * attribute. It is a preset property, not something the Tweaks panel exposes,
 * so `findActiveTheme` does not consider it.
 */
export type ThemeLook = 'glass' | 'console';

export interface ThemePreset {
  label: string;
  sub: string;
  primary: string;
  primaryFg: string;
  secondary: string;
  font: FontFamily;
  bg: BgKey;
  pattern: PatternKey;
  mode?: 'dark';
  /** Defaults to `glass` — the look every preset had before it existed. */
  look?: ThemeLook;
  /**
   * The lighter end of every chart ramp (`--chart-2`). Charts step from the
   * primary toward this. It is NOT the secondary: on Texas Standard, Solar
   * Co-op and Refinery the secondary is an alert hue, and ramping toward it
   * painted the pipeline's success leg in the colour the UI reserves for
   * failure. A preset that names none gets a 55% tint of its primary.
   */
  chart2?: string;
}

export type ThemeKey =
  | 'texas-standard'
  | 'ercot-grid'
  | 'solar-coop'
  | 'refinery'
  | 'midnight-ops';

export type BgKey =
  | 'warm-cream'
  | 'light-sky'
  | 'solar-dawn'
  | 'peach-meadow'
  | 'mint-fog'
  | 'arctic-grid'
  | 'lavender-ash'
  | 'turbine-mist'
  | 'rose-quartz'
  | 'sand-dune'
  | 'volt-paper'
  | 'graphite';

export type PatternKey =
  | 'none'
  | 'grid'
  | 'dots'
  | 'circuit'
  | 'bolts'
  | 'hex'
  | 'waves'
  | 'meter';

export type FontFamily =
  | 'Plus Jakarta Sans'
  | 'Inter'
  | 'DM Sans'
  | 'Manrope'
  | 'IBM Plex Sans'
  | 'Outfit';

export const THEMES: Record<ThemeKey, ThemePreset> = {
  'texas-standard': {
    label: 'Texas Standard',
    sub: 'Broker neutral',
    primary: '#0a0a0c',
    primaryFg: '#ffffff',
    secondary: '#ef4444',
    font: 'Inter',
    bg: 'warm-cream',
    pattern: 'hex',
  },
  'ercot-grid': {
    label: 'ERCOT Grid',
    sub: 'Default · utility operator',
    primary: '#0d3b66',
    primaryFg: '#ffffff',
    secondary: '#0ea5e9',
    font: 'IBM Plex Sans',
    bg: 'arctic-grid',
    // No pattern: a 40px hairline grid under every table competed with the
    // row rules and showed through every bare patch of page. The grid in the
    // name is the power grid, not a texture.
    pattern: 'none',
    look: 'console',
    chart2: '#0ea5e9',
  },
  'solar-coop': {
    label: 'Solar Co-op',
    sub: 'Retail solar',
    primary: '#a16207',
    primaryFg: '#ffffff',
    secondary: '#f59e0b',
    font: 'DM Sans',
    bg: 'solar-dawn',
    pattern: 'dots',
  },
  refinery: {
    label: 'Refinery',
    sub: 'Industrial energy',
    primary: '#1c1c22',
    primaryFg: '#ffffff',
    secondary: '#f97316',
    font: 'Manrope',
    bg: 'sand-dune',
    pattern: 'circuit',
  },
  'midnight-ops': {
    label: 'Midnight Ops',
    sub: 'Dark control room',
    primary: '#60a5fa',
    primaryFg: '#0a0a0c',
    secondary: '#fbbf24',
    font: 'Plus Jakarta Sans',
    bg: 'graphite',
    pattern: 'meter',
    mode: 'dark',
    chart2: '#93c5fd',
  },
};

export const BG_OPTIONS: Record<BgKey, string> = {
  'warm-cream':
    'radial-gradient(circle at 15% 5%, rgba(255,220,200,0.45), transparent 40%), radial-gradient(circle at 85% 0%, rgba(220,225,240,0.30), transparent 45%), linear-gradient(180deg, #ddd8cf 0%, #cac4ba 100%)',
  'light-sky':
    'radial-gradient(circle at 20% 0%, rgba(186,230,253,0.55), transparent 50%), radial-gradient(circle at 90% 90%, rgba(196,181,253,0.32), transparent 55%), linear-gradient(180deg, #f0f9ff 0%, #dbeafe 100%)',
  'solar-dawn':
    'radial-gradient(circle at 80% 10%, rgba(252,211,77,0.50), transparent 45%), radial-gradient(circle at 20% 90%, rgba(251,146,60,0.32), transparent 50%), linear-gradient(180deg, #fef6e7 0%, #f3e3c7 100%)',
  'peach-meadow':
    'radial-gradient(circle at 25% 15%, rgba(254,205,211,0.55), transparent 50%), radial-gradient(circle at 80% 85%, rgba(253,224,71,0.30), transparent 50%), linear-gradient(180deg, #fef2f2 0%, #fde8d6 100%)',
  'mint-fog':
    'radial-gradient(circle at 15% 90%, rgba(167,243,208,0.50), transparent 50%), radial-gradient(circle at 90% 10%, rgba(186,230,253,0.32), transparent 50%), linear-gradient(180deg, #f0fdf4 0%, #d9f0e3 100%)',
  // One hue family. The prototype's version carried a MINT radial bottom-right,
  // which put a green cast under Billing, Quotes and every empty page on a
  // theme whose only meaningful green is the Active pill. Now a single faint
  // wash of the brand navy top-left over a cool blue-grey base.
  'arctic-grid':
    'radial-gradient(circle at 0% 0%, rgba(13,59,102,0.10), transparent 42%), linear-gradient(180deg, #eef2f7 0%, #e3e9f0 100%)',
  'lavender-ash':
    'radial-gradient(circle at 75% 0%, rgba(216,180,254,0.45), transparent 50%), radial-gradient(circle at 15% 100%, rgba(196,181,253,0.32), transparent 55%), linear-gradient(180deg, #faf5ff 0%, #e9e2f0 100%)',
  'turbine-mist':
    'radial-gradient(circle at 50% 0%, rgba(176,222,199,0.45), transparent 50%), radial-gradient(circle at 50% 100%, rgba(180,200,220,0.35), transparent 50%), linear-gradient(180deg, #e8efe8 0%, #cfd9d6 100%)',
  'rose-quartz':
    'radial-gradient(circle at 0% 0%, rgba(251,207,232,0.50), transparent 50%), radial-gradient(circle at 100% 100%, rgba(196,181,253,0.30), transparent 55%), linear-gradient(180deg, #fdf2f8 0%, #efe1ea 100%)',
  'sand-dune':
    'radial-gradient(circle at 30% 0%, rgba(254,215,170,0.50), transparent 50%), radial-gradient(circle at 80% 100%, rgba(217,119,6,0.18), transparent 55%), linear-gradient(180deg, #fef7ed 0%, #f0e2c9 100%)',
  'volt-paper':
    'radial-gradient(circle at 0% 100%, rgba(168,162,158,0.30), transparent 45%), radial-gradient(circle at 100% 0%, rgba(217,119,6,0.18), transparent 50%), linear-gradient(180deg, #f5f1ea 0%, #e7e0d5 100%)',
  graphite:
    'radial-gradient(circle at 30% 0%, rgba(255,255,255,0.06), transparent 45%), radial-gradient(circle at 90% 100%, rgba(96,165,250,0.10), transparent 50%), linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
};

/**
 * ONE FLAT, MIXABLE COLOUR PER `BG_OPTIONS` ENTRY.
 *
 * `BG_OPTIONS` values are three-layer gradient stacks, and `color-mix()` cannot
 * consume a gradient — so until this map existed, nothing in CSS could derive a
 * surface from the page the operator is actually looking at. `--page-bg` in
 * `tokens.css` looks like that colour and is not: nothing writes it at runtime, it
 * is absent from `body.crm-dark`, and it therefore stays a static arctic-grid
 * value for all twelve backgrounds. That inertness is why the AI card's ground
 * had to be cast from the BRAND instead, and why it came out a neutral grey that
 * did not change between presets: `--crm-accent` is near-black for three of the
 * five presets, so 6% of it into white is rgb(240,242,243) whichever preset is
 * active. Six percent of a near-black cannot carry hue. A flat colour that
 * represents the PAGE can.
 *
 * ─── HOW EACH VALUE IS DERIVED ───────────────────────────────────────────────
 * Each one is the AREA AVERAGE of its own entry in `BG_OPTIONS`, composited in CSS
 * paint order: the `linear-gradient` at the end of the list is the bottom layer,
 * and each `radial-gradient` above it contributes its own colour at an alpha that
 * ramps from the stated value at the circle's centre to 0 at its `transparent`
 * stop, over a 16:9 page. Nothing is invented and nothing is borrowed from another
 * entry — change a stop above and the value here moves with it.
 *
 * The base layer's own midpoint was tried first because it is hand-computable, and
 * it is not good enough for the warm entries: it under-states `solar-dawn` by 24
 * RGB units and drops that entry's channel spread from 58 to 34, because the
 * page's character is largely the amber radial sitting on top of the base.
 * Averaging the whole stack is what makes these read as the pages they represent.
 *
 * `arctic-grid` (2026-10-01): base midpoint rgb(232.5, 237.5, 243.5); the navy
 * radial at the corner covers a quarter-disc of radius 0.42 × the farthest
 * corner, which averages to 1.08% alpha over the page → rgb(230, 236, 242).
 *
 * To add a background: paint the gradient on a blank page, screenshot it and take
 * the image's average pixel — or read the base stops and composite the radials by
 * hand. Either way the value belongs to THAT gradient and to no other.
 *
 * ⚠️ This is a DERIVED READING of `BG_OPTIONS`, never an input to it. Nothing here
 * changes what any page renders — the gradients above are untouched, and the only
 * consumer is the `--crm-*` surface family in `styles/crm.css`.
 */
export const BG_SOLIDS: Record<BgKey, string> = {
  'warm-cream': '#d6cfc6',
  'light-sky': '#deecfe',
  'solar-dawn': '#f9e4bf',
  'peach-meadow': '#fee8d7',
  'mint-fog': '#dbf5ea',
  'arctic-grid': '#e6ecf2',
  'lavender-ash': '#ece3f8',
  'turbine-mist': '#d7e2dd',
  'rose-quartz': '#f4e4f1',
  'sand-dune': '#f6e6ce',
  'volt-paper': '#ebe3d7',
  graphite: '#1a2438',
};

export const BG_LABELS: Record<BgKey, string> = {
  'warm-cream': 'Warm cream',
  'light-sky': 'Light sky',
  'solar-dawn': 'Solar dawn',
  'peach-meadow': 'Peach meadow',
  'mint-fog': 'Mint fog',
  'arctic-grid': 'Arctic grid (default)',
  'lavender-ash': 'Lavender ash',
  'turbine-mist': 'Turbine mist',
  'rose-quartz': 'Rose quartz',
  'sand-dune': 'Sand dune',
  'volt-paper': 'Volt paper',
  graphite: 'Graphite (dark)',
};

const encSvg = (svg: string): string =>
  `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;

export interface PatternOption {
  label: string;
  css: string;
  size: string;
}

export const PATTERN_OPTIONS: Record<PatternKey, PatternOption> = {
  none: { label: 'None (clean)', css: 'none', size: 'auto' },
  grid: {
    label: 'Engineering grid',
    css: encSvg(
      '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><path d="M40 0H0V40" fill="none" stroke="rgba(10,10,12,0.06)" stroke-width="1"/></svg>',
    ),
    size: '40px 40px',
  },
  dots: {
    label: 'Quiet dots',
    css: encSvg(
      '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><circle cx="2" cy="2" r="1.2" fill="rgba(10,10,12,0.12)"/></svg>',
    ),
    size: '24px 24px',
  },
  circuit: {
    label: 'Circuit traces',
    css: encSvg(
      '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="none" stroke="rgba(10,10,12,0.09)" stroke-width="1"><path d="M0 20 H30 V0"/><path d="M30 80 V60 H80"/><path d="M50 0 V40 H80"/><circle cx="30" cy="60" r="2" fill="rgba(10,10,12,0.20)"/><circle cx="50" cy="40" r="2" fill="rgba(10,10,12,0.20)"/></svg>',
    ),
    size: '80px 80px',
  },
  bolts: {
    label: 'Lightning bolts',
    css: encSvg(
      '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" fill="none" stroke="rgba(10,10,12,0.10)" stroke-width="1.2" stroke-linejoin="round" stroke-linecap="round"><path d="M30 8L20 30h6l-2 14 16-22h-8z"/></svg>',
    ),
    size: '60px 60px',
  },
  hex: {
    label: 'Hex mesh',
    css: encSvg(
      '<svg xmlns="http://www.w3.org/2000/svg" width="56" height="50" fill="none" stroke="rgba(10,10,12,0.07)" stroke-width="1"><path d="M28 1 L54 14 L54 36 L28 49 L2 36 L2 14 Z"/></svg>',
    ),
    size: '56px 50px',
  },
  waves: {
    label: 'Power waves',
    css: encSvg(
      '<svg xmlns="http://www.w3.org/2000/svg" width="120" height="32" fill="none" stroke="rgba(10,10,12,0.09)" stroke-width="1.2" stroke-linecap="round"><path d="M0 16 Q15 4 30 16 T60 16 T90 16 T120 16"/></svg>',
    ),
    size: '120px 32px',
  },
  meter: {
    label: 'Meter ticks',
    css: encSvg(
      '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><g stroke="rgba(10,10,12,0.10)" stroke-width="1" stroke-linecap="round"><line x1="6" y1="6" x2="6" y2="14"/><line x1="14" y1="6" x2="14" y2="10"/><line x1="22" y1="6" x2="22" y2="10"/><line x1="30" y1="6" x2="30" y2="14"/></g></svg>',
    ),
    size: '40px 40px',
  },
};

export const FONT_OPTIONS: FontFamily[] = [
  'Plus Jakarta Sans',
  'Inter',
  'DM Sans',
  'Manrope',
  'IBM Plex Sans',
  'Outfit',
];

export interface PrimarySwatch {
  bg: string;
  fg: string;
}

export const PRIMARY_SWATCHES: PrimarySwatch[] = [
  { bg: '#0a0a0c', fg: '#ffffff' },
  { bg: '#1a1a22', fg: '#ffffff' },
  { bg: '#0d3b66', fg: '#ffffff' },
  { bg: '#1e3a8a', fg: '#ffffff' },
  { bg: '#15803d', fg: '#ffffff' },
  { bg: '#a16207', fg: '#ffffff' },
  { bg: '#b91c1c', fg: '#ffffff' },
  { bg: '#7c3aed', fg: '#ffffff' },
];

export const SECONDARY_SWATCHES: string[] = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#16a34a',
  '#0ea5e9',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
];

export interface QuickPreset {
  name: string;
  primary: string;
  primaryFg: string;
  secondary: string;
}

export const QUICK_PRESETS: QuickPreset[] = [
  { name: 'Default ink', primary: '#0a0a0c', primaryFg: '#ffffff', secondary: '#ef4444' },
  { name: 'Navy & gold', primary: '#0d3b66', primaryFg: '#ffffff', secondary: '#f59e0b' },
  { name: 'Forest & rust', primary: '#15803d', primaryFg: '#ffffff', secondary: '#b91c1c' },
  { name: 'Indigo & sky', primary: '#1e3a8a', primaryFg: '#ffffff', secondary: '#0ea5e9' },
];

/**
 * The radius tokens each look writes onto :root. `glass` is the prototype's
 * scale; `console` is two steps tighter so cards read as panels of a tool
 * rather than as pebbles. Pills stay pills under both.
 */
export const RADIUS_SCALES: Record<ThemeLook, Readonly<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', string>>> = {
  glass: { xs: '8px', sm: '12px', md: '18px', lg: '24px', xl: '32px' },
  console: { xs: '6px', sm: '10px', md: '14px', lg: '16px', xl: '20px' },
};

export interface Tweaks {
  primary: string;
  primaryFg: string;
  secondary: string;
  font: FontFamily;
  bg: BgKey;
  pattern: PatternKey;
  /** Preset-only; carried in the record so a saved theme keeps its surfaces. */
  look: ThemeLook;
}

/** The theme a CRM user sees before they have picked one of their own. */
export const DEFAULT_THEME_KEY: ThemeKey = 'ercot-grid';

export function tweaksFromTheme(key: ThemeKey): Tweaks {
  const th = THEMES[key];
  return {
    primary: th.primary,
    primaryFg: th.primaryFg,
    secondary: th.secondary,
    font: th.font,
    bg: th.bg,
    pattern: th.pattern,
    look: th.look ?? 'glass',
  };
}

export const TWEAK_DEFAULTS: Tweaks = tweaksFromTheme(DEFAULT_THEME_KEY);

/** The record shape before `look` existed (schema 1 and 2). */
export type LegacyTweaks = Omit<Tweaks, 'look'>;

/** The six fields a schema-1 or schema-2 record could hold. */
export function toLegacyTweaks(t: Tweaks): LegacyTweaks {
  return {
    primary: t.primary,
    primaryFg: t.primaryFg,
    secondary: t.secondary,
    font: t.font,
    bg: t.bg,
    pattern: t.pattern,
  };
}

/**
 * What the CRM shipped as its default under each storage schema version.
 * `useTweaks()` walks a saved record through these: a record still equal to the
 * default that shipped under its own version is a session nobody customised,
 * and is migrated to the current default; anything else is a deliberate pick
 * and is kept. The hook saves on every mount, so the mere presence of a record
 * says nothing about whether anyone actually chose a theme.
 *
 *   1 — Texas Standard.
 *   2 — ERCOT Grid with the engineering-grid pattern and the glass look.
 *   3 — ERCOT Grid, no pattern, console look (`look` added to the record).
 */
export const DEFAULTS_BY_SCHEMA: Readonly<Record<number, LegacyTweaks>> = {
  // Frozen on purpose: these are what the hook SAVED under each version, not
  // what the presets say today. Re-deriving them from THEMES would make the
  // migration drift the next time a preset value moves.
  1: { primary: '#0a0a0c', primaryFg: '#ffffff', secondary: '#ef4444', font: 'Inter', bg: 'warm-cream', pattern: 'hex' },
  2: { primary: '#0d3b66', primaryFg: '#ffffff', secondary: '#0ea5e9', font: 'IBM Plex Sans', bg: 'arctic-grid', pattern: 'grid' },
};

/** `--chart-2` for a record whose preset names no chart colour: a tint of the primary. */
export const CHART_2_FALLBACK = 'color-mix(in srgb, var(--primary) 55%, #ffffff)';

/** The second chart colour a record should paint with — see `ThemePreset.chart2`. */
export function chart2For(t: Tweaks): string {
  const key = findActiveTheme(t);
  return (key !== null ? THEMES[key].chart2 : undefined) ?? CHART_2_FALLBACK;
}

/** The default that shipped before ERCOT Grid took over (schema 1). */
export const LEGACY_TWEAK_DEFAULTS: LegacyTweaks = DEFAULTS_BY_SCHEMA[1] as LegacyTweaks;

export function findActiveTheme(t: Tweaks): ThemeKey | null {
  for (const [key, th] of Object.entries(THEMES) as Array<[ThemeKey, ThemePreset]>) {
    if (th.primary === t.primary && th.bg === t.bg && th.pattern === t.pattern) return key;
  }
  return null;
}
